import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { DosingService } from '../../../services/dosing.service';
import { DailyDosingRateTanksResponse, DRInput, SaveDosingRatesRequest, SaveDosingRatesType } from '../../../models/dosing.model';
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { dateRangeCompleteValidator, numericInputLimit, strictDateValidator } from '../../shared/commonutils/helper';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-addedit-dosingrate',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    DragDropModule
],
  templateUrl: './addedit-dosingrate.component.html',
  styleUrl: './addedit-dosingrate.component.scss'
})
export class AddeditDosingrateComponent implements OnInit {
  
  private service = inject(DosingService);
  private dialog = inject(MatDialog);
  siteId = signal<number>(0);
  displayValue = signal<string>('');
  isEditMode = signal<boolean>(false);
  dialogTitle = signal<string>('Add');
  controlNames: DailyDosingRateTanksResponse[] = [];
  rawTankData: DailyDosingRateTanksResponse[] = []; 
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null, [strictDateValidator]),
    end: new FormControl<Date | null>(null, [strictDateValidator]),
  }, {validators: dateRangeCompleteValidator});
  firstTransferIndex = signal<number>(-1);
  drForm = new FormGroup<{ [key: string]: AbstractControl<any> }>({
    manure: new FormControl('') as AbstractControl
  });
  readonly minDate = new Date();
  canSave: boolean = false;

  constructor(public dialogRef: MatDialogRef<AddeditDosingrateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DRInput) { 
   
    this.isEditMode.set(data.isEditMode);
    this.canSave = data.canSave;
    this.minDate.setHours(0, 0, 0, 0);
    this.dialogTitle.set(this.isEditMode() ? `(${this.data.siteName}) - Edit` : `(${this.data.siteName}) - Add`);
    if(this.data.task === 'Duplicate') {
      this.dialogTitle.set(`(${this.data.siteName}) - Duplicate`);
      this.isEditMode.set(true);
    }
  }
    
  ngOnInit(): void {
    this.siteId.set(this.data.siteId);
    if (this.data.manureToSet !== undefined && this.data.manureToSet !== null) {
      this.drForm.patchValue({
        manure: this.data.manureToSet.toString()
      });
    }
    this.getAllControlNames();
    
    if(this.data.task !== 'Duplicate') {
      const today = this.data.inputDate || new Date();
      this.range.controls['start'].setValue(today);
      this.range.controls['end'].setValue(today);
    }
  }

  getAllControlNames() {
    this.service.getDailyDosingRateTanks(this.siteId()).subscribe({
      next: (data) => {
        this.controlNames = data;
        console.log('Control Names:', this.controlNames);
        this.controlNames = this.controlNames.filter(item => item.tankName !== 'Manure');
        this.rawTankData = data;
        
        // Find the index of the first item that is a tank transfer
        const idx = this.controlNames.findIndex(item => item.isTankTransfer === 1);
        this.firstTransferIndex.set(idx);

        // Add dynamic controls to the form
        this.controlNames.forEach(item => {
          const controlName = this.getControlName(item);
          this.drForm.addControl(controlName, new FormControl(null) as AbstractControl);
        });
        
        if (this.isEditMode()) {
            this.checkAndDisableControls();
            this.patchExistingData();
        }
      }
    });
  }

  getUniqueKey(col: any): string {
    return `${col.id}_${col.isTankTransfer}_${col.tankName}`;
  }
  
  getControlName(col: any): string {
    if (col.isTankTransfer) {
      return `transfer_${col.id}_${col.tankName}`;
    }
    
    // Add 'digester' to the prefix if it is a digester to prevent ID collisions
    const prefix = col.isDigester ? 'digester' : 'tank';
    return `${prefix}_${col.id}`;
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  saveDRData(): void {
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
    
    if(this.range.invalid){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    const formValues = this.drForm.getRawValue();
    const rangeValues = this.range.getRawValue();
    
    const saveDosingRatesTypes: SaveDosingRatesType[] = [];

    // Add dynamic tanks
    this.rawTankData.forEach(tank => {
      let value = 0;      
      if (tank.tankName?.toLowerCase() === 'manure') {
        value = formValues['manure'] || 0;
      } else {
        const ctrlName = this.getControlName(tank);
        value = formValues[ctrlName] || 0;
      }

      saveDosingRatesTypes.push({
        tankId: tank.id,
        doseGpd: value,
        isTankTransfer: !!tank.isTankTransfer, // ensure boolean
        isDigester: !!tank.isDigester         // ensure boolean
      });
    });

    // 2. Build the final Request Object
    const request: SaveDosingRatesRequest = {
      sid: this.siteId().toString(),
      fromDate: rangeValues.start,
      toDate: rangeValues.end,
      saveDosingRatesTypes: saveDosingRatesTypes
    };

    console.log('Final API Request Payload:', request);
    this.service.saveDosingRates(request).subscribe({
      next: (res) => {
        this.dialogRef.close(true);
      }
    });
  }  

  private checkAndDisableControls() {
    const startDate = this.range.get('start')?.value;
    if (!startDate) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const selectedDate = new Date(startDate);
    selectedDate.setHours(0, 0, 0, 0);

    // If selected date is strictly before today
    if (selectedDate.getTime() < today.getTime()) {
      // Disable all tank controls
      console.log('controlNames to disable:', this.controlNames);
      this.controlNames.forEach(item => {
        const controlName = this.getControlName(item);
        this.drForm.get(controlName)?.disable();
      });
      this.drForm.get('manure')?.enable(); 
      this.range.enable();
    } else {
      this.drForm.enable();
    }
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: string): void {
    const input = event.target as HTMLInputElement;
    let val = numericInputLimit(event, maxValue, maxDecimals);
    this.displayValue.set(val);
    
    input.value = val;
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.drForm.get(type)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.drForm.get(type)?.setErrors({ 'incompleteDecimal': true });
    } else {      
      const parsedValue = parseFloat(val);
      this.drForm.get(type)?.setValue(parsedValue, { emitEvent: false });
    }

    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;   // put "14.10" back so next keystroke appends correctly
      }
    });
  }

  private patchExistingData() {
    const existing = this.data.existingData as unknown as Record<string, any>;
    const patchValues: Record<string, any> = {};
    console.log('Existing data for patching:', existing);
    this.controlNames.forEach(item => {
      const controlName = this.getControlName(item);
      // Bridge between JSON key "Hydrolyzer_Dosing" and dynamic control names
      let apiKey = '';

      if (item.isTankTransfer) {
        // Pattern: HT1_HT2_Dosing
        apiKey = item.tankName?.replace('->', '_') + '_Dosing';
      } 
      else if (item.isDigester) {
        apiKey =  item.tankName? item.tankName?.replace('-', '_') : '' + '_Dosing'; 
      } 
      else {
        // Pattern: HT2_Dosing
        apiKey = item.tankName + '_Dosing';
      }
      const valueToPatch = existing[apiKey];
      
      if (existing[apiKey] !== undefined && existing[apiKey] !== null) {
        patchValues[controlName] = valueToPatch;
      }
    });
    console.log('Patching values:', patchValues);
    this.drForm.patchValue(patchValues);
  }  

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }

}
